<script lang="ts" context="module">
    import { z } from "zod";

    const Link = z.object({
        url: z.string().url(),
        target: z.enum(["_blank", "_self", "_parent", "_top"]).optional(),
    });

    type Link = z.infer<typeof Link>;

    export const CustomFields = z.object({
        blink: z.boolean().default(false),
        link: Link.optional(),
        color: z.string().optional(),
        borderColor: z.string().optional(),
        backgroundColor: z.string().optional(),
    });
</script>

<script lang="ts">
    import { getContext } from "svelte";
    import Data from "@reperio-news/core.components/blocks/Title/Data";
    import {
        stripTags,
        replaceContext,
    } from "@reperio-news/core.components/includes/Helpers";
    type Data = z.infer<typeof Data>;

    export let id: string;
    export let type: string;
    export let data: Data;

    const context: SvelteStore<any> = getContext("context");

    let blink: boolean = false;
    let link: Link;
    let color: string;
    let borderColor: string;
    let bgColor: string;

    $: if (data) {
        blink = data.custom_fields?.blink;
        link = data.custom_fields?.link;

        color =
            data.custom_fields?.color ||
            $context.domain_config.MISC.headingsColor;
        borderColor =
            data.custom_fields?.borderColor ||
            $context.domain_config.MISC.headingsBorderColor;

        bgColor =
            data.custom_fields?.backgroundColor ||
            $context.domain_config.MISC.headingsBgColor;
    }
</script>

<!-- svelte-ignore missing-declaration -->
{#if !data || !data.title || !data.element}
    {#if __ADMIN__}
        Title - <strong>needs configuration</strong>
    {/if}
{:else if !data.element || data.element === "h1"}
    <h1
        {id}
        data-type={type}
        class:blinking={blink}
        style="--color: {color}; --borderColor: {borderColor}; --bgColor: {bgColor}"
    >
        {#if link}
            <a href={link.url} target={link.target}
                >{stripTags(replaceContext(data.title, $context))}</a
            >
        {:else}
            {stripTags(replaceContext(data.title, $context))}
        {/if}
    </h1>
{:else if data.element === "h2"}
    <h2
        {id}
        data-type={type}
        class:blinking={blink}
        style="--color: {color}; --borderColor: {borderColor}; --bgColor: {bgColor}"
    >
        {#if link}
            <a href={link.url} target={link.target}
                >{stripTags(replaceContext(data.title, $context))}</a
            >
        {:else}
            {stripTags(replaceContext(data.title, $context))}
        {/if}
    </h2>
{:else if data.element === "h3"}
    <h3
        {id}
        data-type={type}
        class:blinking={blink}
        style="--color: {color}; --borderColor: {borderColor}; --bgColor: {bgColor}"
    >
        {#if link}
            <a href={link.url} target={link.target}
                >{stripTags(replaceContext(data.title, $context))}</a
            >
        {:else}
            {stripTags(replaceContext(data.title, $context))}
        {/if}
    </h3>
{/if}

<style lang="scss">
    h1,
    h2,
    h3 {
        font-family: "Playfair Display", serif;
        font-weight: 900;
        color: var(--color);
        border-bottom: 2px solid var(--borderColor);
        background-color: var(--bgColor);

        a {
            color: inherit;
        }
    }

    h1 {
        font-size: 28px;
    }

    h2 {
        font-size: 24px;
    }

    h3 {
        font-size: 22px;
    }

    .blinking {
        animation: blinking 1s infinite;
    }

    @keyframes blinking {
        0% {
            opacity: 0.5;
        }
        50% {
            opacity: 0.75;
        }
        99% {
            opacity: 0.85;
        }
        100% {
            opacity: 1;
        }
    }
</style>
